import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { FileText, BarChart2, Shuffle } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { DraggableBlocksContainer } from './DraggableBlocksContainer';
import { QuickActionsBar } from './QuickActionsBar';
import { AwaitingMyActionCard } from './AwaitingMyActionCard';
import { usePaymentsList } from '@/features/billing/hooks/usePayments';
import { useBillsList } from '@/features/billing/hooks/useBills';
import { useAccountantReports } from '../hooks/useAccountantReports';
import { useAccountantClientDebt } from '../hooks/useAccountantClientDebt';
import type { StatisticPeriod } from '../api/dashboard.api';

function formatUSD(amount: number | undefined | null) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(
    Number.isFinite(amount) ? (amount as number) : 0
  );
}

function formatPaidAt(iso: string | undefined) {
  if (!iso) return '—';
  const d = new Date(iso);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${mm}/${dd}/${yyyy} ${hh}:${min}`;
}

function clientName(client: { name?: string; lastname?: string } | undefined) {
  if (!client) return null;
  return [client.name, client.lastname].filter(Boolean).join(' ') || null;
}

function formatBillType(type: string | undefined) {
  if (!type) return '—';
  if (type === 'CONTRACT_PERIOD') return 'Contract Period';
  if (type === 'SUCCESS_FEE') return 'Success Fee';
  return type.charAt(0) + type.slice(1).toLowerCase();
}

function formatBillDate(iso: string | undefined) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
}

function AgingItem({ label, value, bg, color }: { label: string; value: string; bg: string; color: string }) {
  return (
    <div className="flex-1 min-w-[120px] px-4 py-3.5 rounded-lg text-center" style={{ backgroundColor: bg, color }}>
      <div className="text-[0.6875rem] font-semibold uppercase tracking-[0.04em] mb-1">{label}</div>
      <div className="text-xl font-bold tabular-nums">{value}</div>
    </div>
  );
}

function PracticeRow({ name, amount, maxAmount }: { name: string; amount: number; maxAmount: number }) {
  const pct = maxAmount > 0 ? (amount / maxAmount) * 100 : 0;
  return (
    <div className="flex items-center gap-3 py-1.5">
      <span className="w-[80px] text-[0.875rem] font-medium text-slate-900 shrink-0">{name}</span>
      <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-700 bg-[#0069D1]" style={{ width: `${pct}%` }} />
      </div>
      <span className="w-16 text-[0.875rem] font-semibold text-right text-slate-900 tabular-nums shrink-0">
        {formatUSD(amount)}
      </span>
    </div>
  );
}

interface AccountantDashboardProps {
  period: StatisticPeriod;
}

export function AccountantDashboard({ period }: AccountantDashboardProps) {
  const { t } = useTranslation('common');

  const { data: draftsData, isLoading: draftsLoading } = useBillsList({ limit: 5, page: 0, state: 'DRAFT' });
  const draftBills = draftsData?.results ?? [];
  const draftTotal = draftsData?.total ?? draftsData?.meta?.total ?? draftBills.length;
  // "To allocate" = advance payments not yet tied to a bill. Keyed on
  // unallocated rather than a status: payments are recorded as ACCEPTED
  // (2026-07-17), so the old status: 'NEW' filter would never match.
  const { data: paymentsData, isLoading: paymentsLoading } = usePaymentsList({ limit: 10, page: 0, unallocated: true });
  const { data: reports, isLoading: reportsLoading } = useAccountantReports(period);
  const { data: clientDebt, isLoading: clientDebtLoading } = useAccountantClientDebt(period);

  const maxPracticeAmount = Math.max(...(reports?.graph ?? []).map(g => g.amount), 0);
  const totalDebt = clientDebt
    ? clientDebt.last_30_day + clientDebt.from_30_to_60 + clientDebt.from_60_to_90 + clientDebt.more_than_90
    : null;

  const blocks = [
    // Case 360° (Stage 12) — draggable like every other dashboard block.
    { id: 'quick-actions', content: <QuickActionsBar /> },
    { id: 'awaiting-my-action', content: <AwaitingMyActionCard /> },
    {
      id: 'invoices',
      content: (
        <Card>
          <CardHeader className="flex-row items-center justify-between pb-3 border-b border-slate-100">
            <div>
              <CardTitle className="flex items-center gap-2 text-base">
                <FileText size={16} className="text-slate-400" /> {t('dashboard.invoicesToIssue')}
              </CardTitle>
              <CardDescription>{t('dashboard.invoicesToIssueDesc')}</CardDescription>
            </div>
            <Link
              to="/app/bills?state=DRAFT"
              className="text-[0.8125rem] font-medium text-[#0069D1] hover:underline shrink-0 whitespace-nowrap"
            >
              {t('dashboard.showAll')}{draftTotal > 0 ? ` (${draftTotal})` : ''} →
            </Link>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="rounded-lg border border-slate-200 divide-y divide-slate-100 overflow-hidden">
              {draftsLoading ? (
                [...Array(5)].map((_, i) => (
                  <div key={i} className="flex items-center gap-3 px-3 py-3">
                    <div className="h-9 w-9 rounded-lg bg-slate-100 animate-pulse shrink-0" />
                    <div className="flex-1 space-y-1.5">
                      <div className="h-3.5 w-24 bg-slate-100 rounded animate-pulse" />
                      <div className="h-3 w-40 bg-slate-100 rounded animate-pulse" />
                    </div>
                    <div className="h-4 w-16 bg-slate-100 rounded animate-pulse" />
                  </div>
                ))
              ) : draftBills.length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-2 px-6 py-10 text-center">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-400">
                    <FileText size={18} />
                  </span>
                  <p className="text-sm text-slate-400">{t('dashboard.noDraftInvoices')}</p>
                </div>
              ) : (
                draftBills.map(bill => {
                  const name = clientName(bill.client);
                  const subParts = [
                    formatBillType(bill.type),
                    bill.dueAt ? `${t('dueAt')}: ${formatBillDate(bill.dueAt)}` : null,
                  ].filter((p): p is string => !!p && p !== '—');
                  return (
                    <Link
                      key={bill.id}
                      to={`/app/bills/${bill.id}`}
                      className="flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 transition-colors"
                    >
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-[#0069D1]">
                        <FileText size={18} />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-[#0069D1] tabular-nums">#{bill.id}</span>
                          <span className="truncate text-[0.875rem] font-medium text-slate-800">
                            {name || bill.contract?.name || t('dashboard.noClient', 'No client')}
                          </span>
                        </div>
                        {subParts.length > 0 && (
                          <div className="truncate text-[0.75rem] text-slate-400">
                            {subParts.join(' · ')}
                          </div>
                        )}
                      </div>
                      <div className="shrink-0 text-right">
                        <div className="font-semibold tabular-nums text-slate-900">{formatUSD(bill.total)}</div>
                        <div className="text-[0.6875rem] text-slate-400">{bill.currency}</div>
                      </div>
                    </Link>
                  );
                })
              )}
            </div>
          </CardContent>
        </Card>
      ),
    },
    {
      id: 'debt',
      content: (
        <Card>
          <CardContent className="p-6">
            <div className="flex items-baseline justify-between mb-5">
              <div>
                <div className="text-[0.75rem] font-medium uppercase tracking-wider text-slate-500">{t('dashboard.totalOutstanding')}</div>
                <div className="text-[1.75rem] font-bold text-slate-900 tabular-nums">
                  {clientDebtLoading || totalDebt === null ? '—' : formatUSD(totalDebt)}
                </div>
              </div>
              {/* <a href="#" className="text-[0.8125rem] font-medium text-[#0069D1] hover:underline">{t('dashboard.seeAll')} →</a> */}
            </div>
            {clientDebtLoading ? (
              <div className="flex items-center justify-center h-[80px] text-slate-400 text-sm">Loading…</div>
            ) : (
              <div className="flex gap-3 flex-wrap">
                <AgingItem label="0–30 days" value={clientDebt ? formatUSD(clientDebt.last_30_day) : '—'} bg="#f0fdf4" color="#15803d" />
                <AgingItem label="30–60 days" value={clientDebt ? formatUSD(clientDebt.from_30_to_60) : '—'} bg="#fffbeb" color="#b45309" />
                <AgingItem label="60–90 days" value={clientDebt ? formatUSD(clientDebt.from_60_to_90) : '—'} bg="#fff7ed" color="#ea580c" />
                <AgingItem label="90+ days" value={clientDebt ? formatUSD(clientDebt.more_than_90) : '—'} bg="#fef2f2" color="#b91c1c" />
              </div>
            )}
          </CardContent>
        </Card>
      ),
    },
    {
      id: 'payments',
      content: (
        <Card>
          <CardHeader className="flex-row items-center justify-between pb-3 border-b border-slate-100">
            <div>
              <CardTitle className="flex items-center gap-2 text-base">
                <Shuffle size={16} className="text-slate-400" /> {t('dashboard.paymentsToAllocate')}
              </CardTitle>
              <CardDescription>{t('dashboard.paymentsToAllocateDesc')}</CardDescription>
            </div>
            {/* <a href="#" className="text-[0.8125rem] font-medium text-[#0069D1] hover:underline shrink-0">{t('dashboard.seeAll')} →</a> */}
          </CardHeader>
          <CardContent className="pt-4">
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    {[t('description'), t('amount'), t('dashboard.statusCol'), t('dashboard.paidAt'), t('client'), t('dashboard.paymentMethod'), t('bill')].map(h => (
                      <th key={h} className="text-left px-2 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {paymentsLoading ? (
                    [...Array(3)].map((_, i) => (
                      <tr key={i} className="border-b border-slate-100 last:border-b-0">
                        {[...Array(7)].map((__, j) => (
                          <td key={j} className="px-2 py-2.5"><div className="h-4 bg-slate-100 rounded animate-pulse" /></td>
                        ))}
                      </tr>
                    ))
                  ) : (
                    (paymentsData?.results ?? []).map(payment => {
                      const name = clientName(payment.client);
                      return (
                        <tr key={payment.id} className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50">
                          <td className="px-2 py-2">
                            <Link to={`/app/payments/${payment.id}`} className="text-blue-700 hover:underline">
                              {payment.description ?? payment.reference ?? `#${payment.id}`}
                            </Link>
                          </td>
                          <td className="px-2 py-2 tabular-nums text-slate-900">
                            {payment.amount.toLocaleString()} {payment.currency}
                          </td>
                          <td className="px-2 py-2">
                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[0.75rem] font-medium bg-blue-50 text-blue-700 border border-blue-100">
                              <span className="text-[10px]">★</span> {payment.status}
                            </span>
                          </td>
                          <td className="px-2 py-2 whitespace-nowrap text-slate-900">{formatPaidAt(payment.paidAt)}</td>
                          <td className="px-2 py-2 text-slate-900">
                            {name && payment.client
                              ? <Link to={`/app/clients/${payment.client.id}`} className="text-blue-700 hover:underline">{name}</Link>
                              : <span className="text-slate-400">—</span>
                            }
                          </td>
                          <td className="px-2 py-2">
                            {payment.paymentMethod
                              ? <span className="text-slate-900">{payment.paymentMethod.name}</span>
                              : <span className="text-slate-400">—</span>
                            }
                          </td>
                          <td className="px-2 py-2">
                            {payment.bill
                              ? <Link to={`/app/bills/${payment.bill.id}`} className="text-blue-700 hover:underline">#{payment.bill.id}</Link>
                              : <span className="text-slate-400">—</span>
                            }
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ),
    },
    {
      id: 'reports',
      content: (
        <Card>
          <CardHeader className="pb-3 border-b border-slate-100">
            <CardTitle className="flex items-center gap-2 text-base">
              <BarChart2 size={16} className="text-slate-400" /> {t('reports')}
            </CardTitle>
            <CardDescription>{t('dashboard.reportsDesc')}</CardDescription>
          </CardHeader>
          <CardContent className="pt-4">
            {reportsLoading ? (
              <div className="flex items-center justify-center h-[160px] text-slate-400 text-sm">Loading…</div>
            ) : (
              <div className="grid gap-4" style={{ gridTemplateColumns: '1fr 2fr' }}>
                <div>
                  <div className="mb-5">
                    <div className="text-[0.75rem] font-medium uppercase tracking-wider text-slate-500">{t('dashboard.collectionRate')}</div>
                    <div className="text-[2rem] font-bold text-green-600 tabular-nums">
                      {reports ? `${reports.collection_rate.toFixed(1)}%` : '—'}
                    </div>
                  </div>
                  <div>
                    <div className="text-[0.75rem] font-medium uppercase tracking-wider text-slate-500">{t('dashboard.writeOffRate')}</div>
                    <div className="text-[2rem] font-bold text-red-600 tabular-nums">
                      {reports ? `${reports.write_off_percent.toFixed(1)}%` : '—'}
                    </div>
                    {reports && reports.write_off > 0 && (
                      <div className="text-[0.8125rem] text-slate-500 mt-0.5">{formatUSD(reports.write_off)} lost</div>
                    )}
                  </div>
                </div>
                <div>
                  <div className="flex items-center gap-2 text-[0.875rem] font-semibold text-slate-900 mb-2">
                    <BarChart2 size={14} className="text-[#0069D1]" /> {t('dashboard.revenueByPracticeYTD')}
                  </div>
                  {(reports?.graph ?? []).map(item => (
                    <PracticeRow
                      key={item.practiceArea}
                      name={item.practiceArea}
                      amount={item.amount}
                      maxAmount={maxPracticeAmount}
                    />
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      ),
    },
  ];

  return (
    <DraggableBlocksContainer storageKey="andersen.accountant.blockOrder" blocks={blocks} />
  );
}
